import {HttpErrorResponse} from '@angular/common/http';
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    Input,
    OnInit,
    OnDestroy,
    PLATFORM_ID,
    QueryList,
    ViewChildren
} from '@angular/core';
import {BackendUserService, ProductsService, QuoteOrderSampleService} from '@coreapiservices';
import {QuoteOrderSampleModel, RequestParamModel} from '@coremodels';
import {NgbActiveModal, NgbModal, NgbPopoverConfig} from '@ng-bootstrap/ng-bootstrap';
import {Constants} from 'src/app/admin-core/constants';
import {AlertService, HelperService, SubscribersService, WebSocketService, DebugLoggerService, AuthService} from "@coreservices";
import {debounceTime, map, takeUntil} from 'rxjs/operators';
import Quill from 'quill';
import {NotificationService} from 'src/app/admin-core/apiservices/notification.service';
import {AdminUserModel} from 'src/app/admin-core/models/admin_user.model';

require("quill-mention");
import {Subject, Subscription} from 'rxjs';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {ConfirmModalComponent} from 'src/app/admin-core/components/confirm-modal/confirm-modal.component';
import {
    UploadSamplePackingImageModalNewComponent
} from 'src/app/sample-requests/sample-request-detail/modal/upload-sample-packing-image-modal-new/upload-sample-packing-image-modal-new.component';
import {isPlatformBrowser} from '@angular/common';
import {Router} from '@angular/router';
import {
    QuoteInvoiceExportModalComponent
} from 'src/app/shared/modal/quote-invoice-export-modal/quote-invoice-export-modal.component';
import {DndDropEvent} from 'ngx-drag-drop';
import {
    calculateConsumableSummary,
    calculateTotalPremiumSealingAndAdhesivePrice
} from 'src/app/admin-core/utils/premium-and-adhesive-calculator';
import {QuoteService} from 'src/app/admin-core/apiservices/quote.service';

@Component({
  standalone: false,
    selector: 'app-preview-detail',
    templateUrl: './preview-detail.component.html',
    styleUrls: ['./preview-detail.component.css', '../../app.component.css', '../../quotes/quote-detail/quote-detail.component.css']
})
export class PreviewDetailComponent implements OnInit, OnDestroy {
    @Input() modelOrderDetail!: any;
    @Input() modelOrderId!: any;
    @Input() isDisplayCalculatedConsumablesSummary!: boolean;
    @Input() isHideConsumablesSummary!: boolean;
    @Input() typeForDownload: any;
    @Input() isStockAllocationSection: any;
    @Input() type: any;
    @Input() shouldExpandComments: boolean = false; // ✅ Flag to auto-expand comment boxes on load
    modelOrder: any;

    /**
     * True when every line is a standalone consumable (consumable_id set, no product_id).
     *
     * Mirrors the server-side predicate. Used to hide the calculator-derived consumables summary,
     * which has no meaning on a quote that has no parent products to calculate against.
     */
    get isConsumablesOnlyOrder(): boolean {
        // Prefer the server-derived flag (the same one order-detail and quote-detail gate on); fall
        // back to inspecting the lines for the preview payloads that don't select it.
        if (this.modelOrder?.is_consumable_order) {
            return true;
        }
        const lines = this.modelOrder?.quote_product ?? [];
        return lines.length > 0 && lines.every((line: any) => !!line?.consumable_id && !line?.product_id);
    }

    isLoading = false;
    isCancelButtonEnabled = true;
    consumableSummary: any = [];
    constants = Constants;
    public isOrderSummaryCollapsed = true;
    consumableSummaryDetail: any;
    private commentUpdateSubject = new Subject<{ comment: string, id: number, oldNote: string }>();
    productDetail: { id: number, note: string }[] = [];
    @Input() isDisplayExpandableContent: boolean;
    containsComment: boolean = false;
    isQuoteCancel: boolean = false;
    isOrderCancel: boolean = false;
    isCommentUpdated: boolean = false;
    @ViewChildren('editorContainer') editorContainers: QueryList<ElementRef>;
    users: { value: string, id: number }[] = [];
    selectedUsers = [];
    removedUsers = [];
    quillEditor: Quill;
    loginUserId: any;
    message: string = "";
    quillEditorContainers = [];
    keyupTimeout: any;
    quoteProductExistingNotifications: any = [];
    profile: AdminUserModel;
    private subscription: Subscription;
    modalRef?: BsModalRef;
    unReadNotificationCount = 0;
    draggedProductIndex = null
    hoveredProductIndex = null;
    isQuoteAccepted = false;
    updateQuillEditorsTrigger: number = 0; // ✅ Trigger to update child's Quill editors
    private destroy$ = new Subject<void>();

    /**
     * Signed rounded-off amount (system final_total − client net paid) when the
     * absolute difference is below the $0.03 rounding tolerance. Returns null
     * when no rounding needs to be displayed. Duplicates OrderSummaryComponent
     * getter because the preview-detail template renders an independent summary.
     */
    get roundedOffAmount(): number | null {
        const order: any = this.modelOrder;
        if (!order || order.type !== this.constants.ORDER_TYPE_ORDER_SHORT) {
            return null;
        }
        // Compare in NET units. Use `total` (or model's `.total` mirror) rather
        // than `final_total` because final_total is gross (incl. surcharge) and
        // quote_payment.amount is net — mixing units would inflate the residual.
        const total = parseFloat(order?.total ?? order?.quote_summary?.total ?? '0');
        if (!total) {
            return null;
        }
        const payments = order?.quote_payment ?? [];
        const netPaid = payments.reduce((sum: number, p: any) => {
            return sum + (parseFloat(p?.amount) || 0);
        }, 0);
        if (netPaid <= 0) {
            return null;
        }
        // Sign convention: positive when overpaid, negative when underpaid.
        // Round to 2dp to avoid FP precision at the tolerance boundary.
        const diff = Number((netPaid - total).toFixed(2));
        if (diff === 0 || Math.abs(diff) > this.constants.PAYMENT_ROUNDING_TOLERANCE) {
            return null;
        }
        return diff;
    }

    constructor(
        private orderService: QuoteOrderSampleService,
        private changeDetection: ChangeDetectorRef,
        public activeModel: NgbActiveModal,
        public helperFunction: HelperService,
        private config: NgbPopoverConfig,
        private alertService: AlertService,
        private subscribersService: SubscribersService,
        private usersService: BackendUserService,
        private notificationService: NotificationService,
        private modalService: NgbModal,
        private modalServices: BsModalService,
        private productServices: ProductsService,
        private router: Router,
        private quoteService: QuoteService,
        private webSocketService: WebSocketService,
        private debugLogger: DebugLoggerService,
        @Inject(PLATFORM_ID) private platformId: any,
        private authService: AuthService
    ) {
        config.placement = 'top',
            config.triggers = 'hover'

        this.commentUpdateSubject.pipe(
            debounceTime(1000)
        ).subscribe(({comment, id, oldNote}) => {
            this.addUpdateQuoteProductComment(comment, id, oldNote);
        });

        this.subscribersService.emitNotificationSidebarToggle(false)
    }

    async ngOnInit(): Promise<void> {
        this.debugLogger.log('🔍 PreviewDetail: ngOnInit - shouldExpandComments:', this.shouldExpandComments);

        await this.getBackendUserList();
        await this.getOrderDetail();

        let userProfile: any = JSON.stringify(this.authService.getAdminUser());
        this.profile = JSON.parse(userProfile);
        this.loginUserId = this.profile.id;

        // Setup WebSocket connection after modelOrderId is available
        if (this.modelOrderId) {
            this.setupWebSocketConnection();
        }
    }

    /**
     * Sets up WebSocket connection and event listeners for real-time updates.
     * Subscribes to quote/order events based on the type of the model.
     */
    private setupWebSocketConnection(): void {
        if (!this.modelOrderId) {
            this.debugLogger.warn('PreviewDetail: Cannot setup WebSocket - no model order ID');
            return;
        }

        this.debugLogger.log('PreviewDetail: Setting up WebSocket connection for order ID:', this.modelOrderId, 'Type:', this.type);

        // Subscribe to appropriate events based on type
        if (this.type === this.constants.ORDER_TYPE_QUOTE_SHORT) {
            // Subscribe to quote events
            this.debugLogger.log('PreviewDetail: Subscribing to quote events for quote ID:', this.modelOrderId);
            this.webSocketService.getQuoteEvents()
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (event) => {
                        this.debugLogger.log('🔄 PreviewDetail: WebSocket quote event received - refreshing data:', event);
                        // Refresh data without showing loader to maintain smooth UX
                        this.handleGetOrderDetail();
                    },
                    error: (error) => {
                        this.debugLogger.error('PreviewDetail: Error in quote events subscription:', error);
                    },
                    complete: () => {
                        this.debugLogger.log('PreviewDetail: Quote events subscription completed');
                    }
                });
        } else if (this.type === this.constants.ORDER_TYPE_ORDER_SHORT) {
            // Subscribe to order events
            this.debugLogger.log('PreviewDetail: Subscribing to order events for order ID:', this.modelOrderId);
            this.webSocketService.getQuoteEvents()
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (event) => {
                        this.debugLogger.log('🔄 PreviewDetail: WebSocket order event received - refreshing data:', event);
                        // Refresh data without showing loader to maintain smooth UX
                        this.handleGetOrderDetail();
                    },
                    error: (error) => {
                        this.debugLogger.error('PreviewDetail: Error in order events subscription:', error);
                    },
                    complete: () => {
                        this.debugLogger.log('PreviewDetail: Order events subscription completed');
                    }
                });
        } else if (this.type === this.constants.ORDER_TYPE_SAMPLE_SHORT) {
            // Subscribe to sample request events
            this.debugLogger.log('PreviewDetail: Subscribing to sample events for sample ID:', this.modelOrderId);
            this.webSocketService.getSampleEvents()
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (event) => {
                        this.debugLogger.log('🔄 PreviewDetail: WebSocket sample event received - refreshing data:', event);
                        // Refresh data without showing loader to maintain smooth UX
                        this.handleGetOrderDetail();
                    },
                    error: (error) => {
                        this.debugLogger.error('PreviewDetail: Error in sample events subscription:', error);
                    },
                    complete: () => {
                        this.debugLogger.log('PreviewDetail: Sample events subscription completed');
                    }
                });
        }

        // Subscribe to connection state changes
        this.webSocketService.getConnectionState()
            .pipe(takeUntil(this.destroy$))
            .subscribe((state) => {
                this.debugLogger.log('PreviewDetail: WebSocket connection state:', state);
                if (state === 'connected' && this.modelOrderId) {
                    // Rejoin room on reconnection based on type
                    const roomPrefix = this.type === this.constants.ORDER_TYPE_QUOTE_SHORT ? 'quote_' :
                        this.type === this.constants.ORDER_TYPE_ORDER_SHORT ? 'quote_' :
                            this.type === this.constants.ORDER_TYPE_SAMPLE_SHORT ? 'sample_' : '';
                    if (roomPrefix) {
                        this.webSocketService.joinRoom(roomPrefix + this.modelOrderId);
                    }
                }
            });
    }

    /**
     * Handle data refresh from socket events while preserving UI state.
     * This method is called when socket events are received to update the data
     * without showing the loader and without disrupting the user's current view.
     */
    private handleGetOrderDetail(): void {
        this.debugLogger.log('🔄 PreviewDetail: handleGetOrderDetail called - preserving UI state');

        // Store current UI state before refresh
        const expandedComments = this.modelOrder?.quote_product?.map(p => ({
            id: p.id,
            isCommentExpanded: p.isCommentExpanded,
            isStockExpanded: p.isStockExpanded,
            isStatusSectionVisible: p.isStatusSectionVisible
        })) || [];

        // Refresh data without showing loader
        const requestParamContestModel = new RequestParamModel();

        // Use same expands as getOrderDetail
        if (this.isDisplayCalculatedConsumablesSummary) {
            requestParamContestModel.expands = ['quote_product.product', 'orderNotAppliedConsumableCalculators'];
        } else {
            const commonExpands = [
                'status', 'company', 'quoteOwner', 'quoteOwner.backend_user',
                'activity', 'activity.status', 'quote_address', 'quote_product',
                'quote_product.notification', 'quote_product.product', 'quote_product.activity',
                'quote_product.product.material', 'quote_product.product.finish',
                'quote_product.product.edge', 'quote_product.product.family',
                'quote_logistic', 'quote_summary', 'leadTime'
            ];

            const quoteShortExtraExpands = [
                'company.company_address', 'quote_address.instruction', 'quote_product.product.unit_of_measurement',
                'quote_product.product.join_product_type_collection_category.product_collection',
                'quote_product.product.join_product_type_collection_category.product_category',
                'quote_product.product.product_image', 'quote_product.quote_product_applied_calculator',
                'quoteProductCalculators', 'schedulePayments', 'backend_user',
                'credit_specifier.company', 'quote_site_contact', 'defaultQuoteAddress',
                'daysToExpiry', 'pendingTasks', 'linkedQuoteOrders', 'isDepositPaymentPaid',
                'isFinalPaymentPaid', 'isFullPaymentPaid', 'totalAdhesiveCalculatorPrice',
                'totalSealerCalculatorPrice', 'quote_product.product.accessory_profile', 'quote_product.product.accessory_category'
            ];

            if (this.isStockAllocationSection) {
                quoteShortExtraExpands.push('quoteOverallSealerUnit');
            }

            const defaultQuoteExtraExpands = [
                'acceptedActivity', 'lastBigPostDeliveryDetails', 'quoteFeedback',
                'quoteOwner.business_role', 'quote_product.product.accessory_profile',
                'quote_product.product.join_product_type_collection_category.product_collection',
                'quote_product.product.join_product_type_collection_category.product_category',
                'quote_product.product.sample_product', 'quote_product.product.sample_product.edge',
                'quote_product.product.sample_product.family', 'quote_product.product.sample_product.sample_product_image',
                'quote_product.sample_packing_image', 'quote_logistic.logistic_type', 'quote_payment',
                'quote_schedule_delivery.carrier_status', 'quote_schedule_delivery.carrier',
                'quote_schedule_delivery.carrier.carrier_region', 'frontendUser.businessRole',
                'quoteOrderSentEmail', 'stockRequiredDateChangeDetail', 'quoteOrderStatusList',
                'quoteStatusActivity', 'quoteStatusLog', 'trade_discount_percentage',
                'quote_product.product.formatHeroImage', 'quote_product.product.familyHeroImage',
                'quote_product.product.format', 'quote_product.product.thikness',
                'quote_product.product.height', 'quote_product.product.opening',
                'quote_product.product.diameter', 'quote_product.product.weight',
                'quote_product.product.category_name', 'quote_product.product.is_feature_profile',
                'quote_product.product.category_id', 'quote_product.product.category',
                'quote_product.product.sampleEdge', 'quote_product.product.sampleFamily',
                'quote_product.product.sampleFormat', 'quote_product.product.sampleFormatHeroImage',
                'quote_product.product.productCollection', 'quote_product.product.productCategory',
                'quote_product.product.sampleProduct', 'quote_product.product.sampleProduct.formatHeroImage',
                'quote_product.product.accessoryCategory', 'quote_product.quoteProductCalculators',
                'quote_product.quoteProductAppliedCalculators', 'quoteOverallSealerUnit', 'quoteProductAppliedCalculators', 'quote_product.product.accessory_profile', 'quote_product.product.accessory_category'
            ];

            if (this.type === this.constants.ORDER_TYPE_QUOTE_SHORT || this.type === this.constants.ORDER_TYPE_ORDER_SHORT || this.isStockAllocationSection) {
                requestParamContestModel.expands = [...commonExpands, ...quoteShortExtraExpands];
            } else {
                requestParamContestModel.expands = [...commonExpands, ...defaultQuoteExtraExpands];
            }

            if (this.isStockAllocationSection) {
                requestParamContestModel.expands.push(
                    'quote_product.product.unAssignedStock',
                    'quote_product.product.onHandStock',
                    'quote_product.product.containers'
                );
            }

            requestParamContestModel.addDefaultFilter(
                this.type === this.constants.ORDER_TYPE_SAMPLE_SHORT
                    ? 'quote_product.product.sample_product.sample_product_image.type'
                    : 'quote_product.product.product_image.type',
                this.constants.FORMAT_HERO_IMAGE,
                'number'
            );

            requestParamContestModel.addDefaultFilter(
                'company.company_address.is_default',
                this.constants.IS_Y,
                'number'
            );
        }

        this.quoteService.getQuoteDetail(
            this.modelOrderId ? this.modelOrderId : this.modelOrderDetail.id,
            requestParamContestModel
        ).subscribe({
            next: (dataList: QuoteOrderSampleModel) => {
                const sortedFilteredProducts = (dataList.quote_product ?? [])
                    .filter(item => item.quantity > 0)
                    .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
                    .map((item, index) => {
                        let oldPrice = 0;
                        if (item.ul_product_code) {
                            oldPrice = item?.price;
                        } else {
                            oldPrice = item.product.price;
                        }

                        const newPrice = item.price;
                        const discount = (newPrice < oldPrice && newPrice > 0)
                            ? Number((((oldPrice - newPrice) / oldPrice) * 100).toFixed(2))
                            : 0;

                        if (item.notification?.length) {
                            this.quoteProductExistingNotifications[item.id] = item.notification;
                        }

                        // Find previous state for this product
                        const previousState = expandedComments.find(p => p.id === item.id);

                        let ulProductDescription = '';
                        if (item.ul_product_description) {
                            let formatedDescription = JSON.parse(item.ul_product_description);
                            ulProductDescription = formatedDescription?.ProductDescription ?? '';
                        }
                        item.ulProductDescription = ulProductDescription;

                        return {
                            ...item,
                            order_index: index + 1,
                            // ✅ Preserve UI state from before refresh
                            isCommentExpanded: previousState?.isCommentExpanded ?? false,
                            isStockExpanded: previousState?.isStockExpanded ?? false,
                            isStatusSectionVisible: previousState?.isStatusSectionVisible ?? false,
                            old_note: item.note,
                            original_sku_price: newPrice,
                            updated_sku_price: newPrice,
                            applied_discount: discount,
                            is_discount: discount > 0
                        };
                    });

                this.modelOrder = {
                    ...dataList,
                    quote_product: sortedFilteredProducts
                };

                const activities = this.modelOrder.activity ?? [];
                this.isQuoteAccepted = !!this.modelOrder.acceptedActivity?.id;
                this.isQuoteCancel = activities.some(a => a.status?.short_name === Constants.QUOTE_CANCELLED_SHORT);
                this.isOrderCancel = activities.some(a => a.status?.short_name === Constants.ORDER_CANCELLED_SHORT);

                this.hasProductWithComment();
                this.openConsumableCalculationPopUp();
                this.calculateConsumableSummary();
                this.calculateTotalPremiumSealingAndAdhesivePrice();

                // ✅ Update Quill editors with latest data while preserving state
                setTimeout(() => {
                    this.debugLogger.log('📝 PreviewDetail: Updating Quill editors with latest data from socket event');

                    // ✅ Trigger child component to update Quill editors
                    // Increment the trigger to notify child component via @Input change detection
                    this.updateQuillEditorsTrigger++;
                    this.debugLogger.log('📡 PreviewDetail: Triggered child Quill editor update:', this.updateQuillEditorsTrigger);

                    // Update parent's Quill editors (if any are initialized in parent)
                    this.updateQuillEditorsContent();
                    this.changeDetection.detectChanges();
                }, 0);

                this.debugLogger.log('✅ PreviewDetail: Data refreshed successfully with UI state preserved');
            },
            error: (error) => {
                this.debugLogger.error('PreviewDetail: Error refreshing data from socket event:', error);
            }
        });
    }

    /**
     * ✅ Update existing Quill editors with new comment content after data refresh
     * This ensures that when socket events update the data, the Quill editors show the new content
     */
    updateQuillEditorsContent() {
        this.debugLogger.log('🔄 PreviewDetail: updateQuillEditorsContent called');

        if (!this.modelOrder?.quote_product) {
            this.debugLogger.log('⚠️ PreviewDetail: No quote_product found');
            return;
        }

        if (!this.quillEditorContainers) {
            this.debugLogger.log('⚠️ PreviewDetail: No quillEditorContainers found');
            return;
        }

        this.debugLogger.log('📊 PreviewDetail: Available Quill editors:', Object.keys(this.quillEditorContainers));
        this.debugLogger.log('📊 PreviewDetail: Products to update:', this.modelOrder.quote_product.map(p => p.id));

        this.modelOrder.quote_product.forEach((product: any) => {
            // Find the Quill editor for this product using product ID as key
            const quillEditor = this.quillEditorContainers[product.id];

            if (!quillEditor) {
                this.debugLogger.log(`⚠️ PreviewDetail: No Quill editor found for product ${product.id} (comment section may be collapsed)`);
                return;
            }

            // Get current content to compare
            const currentContent = quillEditor.root.innerHTML;
            const newContent = product.note || '';

            // Only update if content has changed to avoid unnecessary updates
            if (currentContent !== newContent) {
                this.debugLogger.log(`📝 PreviewDetail: Updating Quill editor content for product ${product.id}`);
                this.debugLogger.log(`   Old content: "${currentContent.substring(0, 50)}..."`);
                this.debugLogger.log(`   New content: "${newContent.substring(0, 50)}..."`);

                // Update the editor content
                if (newContent) {
                    quillEditor.setContents(
                        quillEditor.clipboard.convert({ html: newContent })
                    );

                    // Update selected users for mentions
                    const editorContent = this.getContentWithMentions(product.id);
                    this.updateSelectedUsers(editorContent, product.id, true);

                    this.debugLogger.log(`✅ PreviewDetail: Successfully updated Quill editor for product ${product.id}`);
                } else {
                    // ✅ Clear the editor if note is empty - use setContents with empty delta
                    this.debugLogger.log(`📝 PreviewDetail: Clearing Quill editor for product ${product.id} - note is empty`);
                    quillEditor.setContents([{insert: '\n'}]);
                    quillEditor.root.innerHTML = '';

                    // ✅ Clear selected users for this product
                    if (this.selectedUsers && this.selectedUsers[product.id]) {
                        this.selectedUsers[product.id] = [];
                    }
                }
            } else {
                this.debugLogger.log(`✅ PreviewDetail: Product ${product.id} content already up to date`);
            }
        });

        this.debugLogger.log('✅ PreviewDetail: Finished updating all Quill editors');
    }

    ngOnDestroy() {
        this.debugLogger.log('🧹 PreviewDetail: ngOnDestroy called - cleaning up subscriptions and WebSocket connections');

        // ✅ Complete the destroy$ subject to unsubscribe all observables using takeUntil
        this.destroy$.next();
        this.destroy$.complete();

        // ✅ Leave WebSocket room to stop receiving events
        if (this.modelOrderId && this.type) {
            const roomPrefix = this.type === this.constants.ORDER_TYPE_QUOTE_SHORT ? 'quote_' :
                this.type === this.constants.ORDER_TYPE_ORDER_SHORT ? 'quote_' :
                    this.type === this.constants.ORDER_TYPE_SAMPLE_SHORT ? 'sample_' : '';
            if (roomPrefix) {
                this.debugLogger.log('📡 PreviewDetail: Leaving WebSocket room:', roomPrefix + this.modelOrderId);
                this.webSocketService.leaveRoom(roomPrefix + this.modelOrderId);
            }
        }

        // ✅ Unsubscribe from any remaining subscriptions
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    cancel() {
        this.isCommentUpdated ? this.activeModel.close(true) : this.activeModel.close(false);
    }

    getOrderDetail() {
        return new Promise((resolve, reject) => {
            this.isLoading = true;
            const requestParamContestModel = new RequestParamModel();
            // Case: Consumables Summary — handle early
            if (this.isDisplayCalculatedConsumablesSummary) {
                requestParamContestModel.expands = ['quote_product.product', 'orderNotAppliedConsumableCalculators'];
            } else {
                // Define all expands outside so they are accessible everywhere
                const commonExpands = [
                    'status', 'company', 'quoteOwner', 'quoteOwner.backend_user',
                    'activity', 'activity.status', 'quote_address', 'quote_product',
                    'quote_product.notification', 'quote_product.product', 'quote_product.activity',
                    'quote_product.product.material', 'quote_product.product.finish',
                    'quote_product.product.edge', 'quote_product.product.family',
                    'quote_logistic', 'quote_summary', 'leadTime', 'quote_product.consumables_prices', 'calculatedLeadTime',
                    'quote_product.product.sample_product.sample_product_description'
                ];

                const quoteShortExtraExpands = [
                    'company.company_address', 'quote_address.instruction', 'quote_product.product.unit_of_measurement',
                    'quote_product.product.join_product_type_collection_category.product_collection',
                    'quote_product.product.join_product_type_collection_category.product_category',
                    'quote_product.product.product_image', 'quote_product.quote_product_applied_calculator',
                    'quoteProductCalculators', 'schedulePayments', 'backend_user',
                    'credit_specifier.company', 'quote_site_contact', 'defaultQuoteAddress',
                    'daysToExpiry', 'pendingTasks', 'linkedQuoteOrders', 'isDepositPaymentPaid',
                    'isFinalPaymentPaid', 'isFullPaymentPaid', 'totalAdhesiveCalculatorPrice',
                    'totalSealerCalculatorPrice', 'quote_product.product.accessory_profile', 'quote_product.product.accessory_category', 'quote_product.quote_schedule_delivery_product.quote_schedule_delivery'
                ];

                if (this.isStockAllocationSection) {
                    quoteShortExtraExpands.push('quoteOverallSealerUnit');
                }

                const defaultQuoteExtraExpands = [
                    'acceptedActivity', 'lastBigPostDeliveryDetails', 'quoteFeedback',
                    'quoteOwner.business_role', 'quote_product.product.accessory_profile',
                    'quote_product.product.join_product_type_collection_category.product_collection',
                    'quote_product.product.join_product_type_collection_category.product_category',
                    'quote_product.product.sample_product', 'quote_product.product.sample_product.edge',
                    'quote_product.product.sample_product.family', 'quote_product.product.sample_product.sample_product_image',
                    'quote_product.sample_packing_image', 'quote_logistic.logistic_type', 'quote_payment',
                    'quote_schedule_delivery.carrier_status', 'quote_schedule_delivery.carrier',
                    'quote_schedule_delivery.carrier.carrier_region', 'frontendUser.businessRole',
                    'quoteOrderSentEmail', 'stockRequiredDateChangeDetail', 'quoteOrderStatusList',
                    'quoteStatusActivity', 'quoteStatusLog', 'trade_discount_percentage',
                    'quote_product.product.formatHeroImage', 'quote_product.product.familyHeroImage',
                    'quote_product.product.format', 'quote_product.product.thikness',
                    'quote_product.product.height', 'quote_product.product.opening',
                    'quote_product.product.diameter', 'quote_product.product.weight',
                    'quote_product.product.category_name', 'quote_product.product.is_feature_profile',
                    'quote_product.product.category_id', 'quote_product.product.category',
                    'quote_product.product.sampleEdge', 'quote_product.product.sampleFamily',
                    'quote_product.product.sampleFormat', 'quote_product.product.sampleFormatHeroImage',
                    'quote_product.product.productCollection', 'quote_product.product.productCategory',
                    'quote_product.product.sampleProduct', 'quote_product.product.sampleProduct.formatHeroImage',
                    'quote_product.product.accessoryCategory', 'quote_product.quoteProductCalculators',
                    'quote_product.quoteProductAppliedCalculators', 'quoteOverallSealerUnit', 'quoteProductAppliedCalculators', 'quote_product.product.accessory_profile', 'quote_product.product.accessory_category', 'quote_product.quote_schedule_delivery_product.quote_schedule_delivery'
                ];

                // Apply expands based on type
                if (this.type === this.constants.ORDER_TYPE_QUOTE_SHORT || this.type === this.constants.ORDER_TYPE_ORDER_SHORT || this.isStockAllocationSection) {
                    requestParamContestModel.expands = [...commonExpands, ...quoteShortExtraExpands];
                } else {
                    requestParamContestModel.expands = [...commonExpands, ...defaultQuoteExtraExpands];
                }

                // Add stock-specific fields if enabled
                if (this.isStockAllocationSection) {
                    requestParamContestModel.expands.push(
                        'quote_product.product.unAssignedStock',
                        'quote_product.product.onHandStock',
                        'quote_product.product.containers'
                    );
                }

                // Add filters
                requestParamContestModel.addDefaultFilter(
                    this.type === this.constants.ORDER_TYPE_SAMPLE_SHORT
                        ? 'quote_product.product.sample_product.sample_product_image.type'
                        : 'quote_product.product.product_image.type',
                    this.constants.FORMAT_HERO_IMAGE,
                    'number'
                );

                requestParamContestModel.addDefaultFilter(
                    'company.company_address.is_default',
                    this.constants.IS_Y,
                    'number'
                );
            }

            // 🔁 Make API call with constructed params
            this.quoteService.getQuoteDetail(
                this.modelOrderId ? this.modelOrderId : this.modelOrderDetail.id,
                requestParamContestModel
            ).subscribe({
                next: (dataList: QuoteOrderSampleModel) => {
                    this.isLoading = false;

                    // Process products: filter, enrich with additional properties
                    const processedProducts = (dataList.quote_product ?? [])
                        .filter(item => item.quantity > 0)
                        .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
                        .map((item, index) => {
                            let oldPrice = 0;
                            if (item.consumable_id) {
                                oldPrice = item?.price;
                            } else {
                                oldPrice = item.product.price;
                            }

                            const newPrice = item.price;
                            const discount = (newPrice < oldPrice && newPrice > 0)
                                ? Number((((oldPrice - newPrice) / oldPrice) * 100).toFixed(2))
                                : 0;

                            if (item.notification?.length) {
                                this.quoteProductExistingNotifications[item.id] = item.notification;
                            }

                            this.productDetail.push({id: item.id, note: item.note});

                            let ulProductDescription = '';
                            if (item.ul_product_description) {
                                let formatedDescription = JSON.parse(item.ul_product_description);
                                ulProductDescription = formatedDescription?.ProductDescription ?? '';
                            }
                            item.ulProductDescription = ulProductDescription;

                            const shouldExpand = this.shouldExpandComments && !!item.note;
                            this.debugLogger.log(`🔍 PreviewDetail: Product ${item.id} - shouldExpandComments: ${this.shouldExpandComments}, hasNote: ${!!item.note}, isCommentExpanded: ${shouldExpand}`);

                            return {
                                ...item,
                                // ✅ Auto-expand comments if shouldExpandComments is true AND product has a comment
                                isCommentExpanded: shouldExpand,
                                isStockExpanded: false,
                                isStatusSectionVisible: false,
                                old_note: item.note,
                                original_sku_price: newPrice,
                                updated_sku_price: newPrice,
                                applied_discount: discount,
                                is_discount: discount > 0
                            };
                        });

                    // ✅ Sort products and assign displayIndex (only for Quote/Order, not Sample)
                    let sortedProducts;
                    if (this.type === this.constants.ORDER_TYPE_QUOTE_SHORT || this.type === this.constants.ORDER_TYPE_ORDER_SHORT) {
                        sortedProducts = this.sortQuoteProducts(processedProducts);
                    } else {
                        // For Sample requests, use simple index-based numbering
                        sortedProducts = processedProducts
                            .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
                            .map((item, index) => ({
                                ...item,
                                order_index: index + 1,
                                displayIndex: String(index + 1)
                            }));
                    }

                    this.modelOrder = {
                        ...dataList,
                        quote_product: sortedProducts
                    };

                    const activities = this.modelOrder.activity ?? [];
                    this.isQuoteAccepted = !!this.modelOrder.acceptedActivity?.id;
                    this.isQuoteCancel = activities.some(a => a.status?.short_name === Constants.QUOTE_CANCELLED_SHORT);
                    this.isOrderCancel = activities.some(a => a.status?.short_name === Constants.ORDER_CANCELLED_SHORT);

                    this.hasProductWithComment();
                    this.openConsumableCalculationPopUp();
                    this.changeDetection.detectChanges();
                    this.calculateConsumableSummary();
                    this.calculateTotalPremiumSealingAndAdhesivePrice();

                    resolve(true);
                },
                error: () => {
                    this.isLoading = false;
                    reject(false);
                }
            });
        });
    }

    openConsumableCalculationPopUp() {
        if (this.isDisplayCalculatedConsumablesSummary) {
            this.consumableSummaryDetail = this.modelOrder?.orderNotAppliedConsumableCalculators;
        } else {
            this.consumableSummaryDetail = this.modelOrder?.quoteOverallSealerUnit;
        }
    }

    productImage(productData: any) {
        let imageUrl: any;
        if (productData?.product?.formatHeroImage) {
            imageUrl = productData.product.formatHeroImage;
            return imageUrl?.path ? Constants.S3_PRODUCT_IMAGES_IMAGE_KIT_URL + '/' + imageUrl.path + Constants.IMAGE_SIZE_62_X_62 : '';
        } else if (productData?.product?.familyHeroImage) {
            imageUrl = productData?.product?.familyHeroImage;
            return imageUrl?.path ? Constants.S3_PRODUCT_IMAGES_IMAGE_KIT_URL + '/' + imageUrl.path + Constants.IMAGE_SIZE_62_X_62 : '';
        } else {
            return ''
        }
    }


    formatDimension(productDimension: any) {
        var formatedDimension = '';
        if (productDimension) {
            formatedDimension = productDimension.replace("mm", "");
        }

        return formatedDimension;
    }

    /************************************
     * Consumable Calculator functions start
     ************************************/

    calculateConsumableSummary() {
        this.consumableSummary = calculateConsumableSummary(this.modelOrder.quote_product, Constants.CONSUMABLE_CALCULATOR_TYPE_DEFENDER, Constants.CONSUMABLE_CALCULATOR_TYPE_GOLD)
        this.changeDetection.detectChanges();
    }

    calculateTotalPremiumSealingAndAdhesivePrice() {
        calculateTotalPremiumSealingAndAdhesivePrice(this.modelOrder.quote_product, Constants.CONSUMABLE_CALCULATOR_TYPE_ADHESIVE, Constants.CONSUMABLE_CALCULATOR_TYPE_DEFENDER, Constants.CONSUMABLE_CALCULATOR_TYPE_GOLD);
        this.changeDetection.detectChanges();
    }

    /************************************
     * Consumable Calculator functions end
     ************************************/

    // calculateTradePrice(total_unit, total_price) {
    //   if (total_unit > 0) {
    //     let tradePrice = this.safeValue(total_price) / this.safeValue(total_unit);
    //     return tradePrice
    //   } else {
    //     return 0;
    //   }
    // }
    calculatePrice(total_price?: number): number {
        if (!total_price || isNaN(total_price)) {
            return 0; // Return 0 if total_price is null, undefined, or not a number
        }
        const price = total_price;
        return parseFloat(price.toFixed(2)); // Convert to number with 2 decimal places
    }

    safeValue = (value, defaultValue = 0) => value != null ? value : defaultValue;

    /************************************
     * Quote product comment functions start
     ************************************/

    hasProductWithComment() {
        this.containsComment = this.modelOrder?.quote_product?.some(item => item.note && item.note.trim());
    }

    onCommentKeyUp(comment: string, id: number, oldNote: string) {
        this.isCommentUpdated = true;
        this.commentUpdateSubject.next({comment, id, oldNote});
        this.hasProductWithComment();
    }

    removeSpaces(input: string): string {
        // Remove HTML tags
        let noHtml = input.replace(/<[^>]*>/g, '');
        // Remove spaces
        return noHtml.replace(/\s+/g, '');
    }

    addUpdateQuoteProductComment(quoteProductComment: any, productId: number, oldNote) {
        const trimmedValue = this.removeSpaces(quoteProductComment);

        // Condition 1: If oldNote is empty and user adds only spaces
        if (!oldNote && !trimmedValue) {
            return;
        }

        // Condition 2: If oldNote is empty and user adds spaces with some digit or character
        if (!oldNote && trimmedValue) {
            this.callQuoteProductCommentApi(quoteProductComment, productId);
            return;
        }

        // Condition 3: If oldNote is not empty and user removes value of quoteProductComment
        if (oldNote && !trimmedValue) {
            this.callQuoteProductCommentApi(quoteProductComment, productId);
            return;
        }

        // Condition 4: If oldNote is not empty, user removes value, and then adds spaces or some digit/character
        if (oldNote && quoteProductComment.trim() !== oldNote.trim()) {
            if (!trimmedValue) {
                return;
            } else {
                this.callQuoteProductCommentApi(quoteProductComment, productId);
                return;
            }
        }
    }

    callQuoteProductCommentApi(comment: string, id: number): void {
        let json = {
            note: comment,
        };

        let modifiedproduct = this.getProductById(id);
        this.quoteService.updateQuoteProduct(json, id).subscribe(
            (res: any) => {
                if (!modifiedproduct.note) {
                    this.alertService.success(Constants.ADD_COMMENT_SUCCESS_MESSAGE);
                } else {
                    this.alertService.success(Constants.UPDATE_COMMENT_SUCCESS_MESSAGE);
                }
                this.isCancelButtonEnabled = true;
                const note = res.note;
                if (note.includes('@')) {
                    this.sendNotification(res)
                }
                /* ################### Delete Notification For Removed User Start ################### */
                if (Array.isArray(this.removedUsers[res.id]) && this.removedUsers[res.id].length > 0) {
                    this.removedUsers[res.id].forEach((removedUser: any, index: number) => {
                        if (this.quoteProductExistingNotifications && this.quoteProductExistingNotifications.length > 0 && this.quoteProductExistingNotifications[res.id]) {
                            var existingNotificationObj = this.quoteProductExistingNotifications[res.id].find(notification => notification.quote_product_id === res.id && notification.to_user_id === removedUser.id);
                            if (existingNotificationObj && existingNotificationObj.id > 0) {
                                // Delete the notification
                                this.notificationService.deleteNotification(existingNotificationObj.id).subscribe((deleteResponse) => {
                                    // Remove the user from removedUsers array after successful deletion
                                    this.removedUsers[res.id].splice(index, 1);
                                    this.subscribersService.updateUnreadNotification(this.unReadNotificationCount.toString());
                                    // Handle delete response if needed
                                }, error => {
                                    // Handle error if needed
                                });
                            }
                        }
                    });
                }
                /* ################### Delete Notification For Removed User End ################### */
                const responseProductId = res.id;
                const responseUpdatedNote = res.note;

                var productToUpdate = this.modelOrder.quote_product.find(item => item.id === responseProductId);
                if (productToUpdate) {
                    productToUpdate.old_note = this.removeSpaces(responseUpdatedNote);
                    productToUpdate.note = responseUpdatedNote;
                }

                // ✅ Emit socket event for product comment update
                this.debugLogger.log('💬 PreviewDetail: Product comment added/updated - emitting socket event');
                this.webSocketService.emit('quote_updated', {
                    quoteId: this.modelOrder.id,
                    exportId: this.modelOrder.export_id
                });

                // Emit appropriate event based on type
                if (this.type === this.constants.ORDER_TYPE_ORDER_SHORT) {
                    this.webSocketService.emit('order_created', {
                        quoteId: this.modelOrder.id,
                        exportId: this.modelOrder.export_id,
                        userId: this.modelOrder.frontend_user_id
                    });
                } else if (this.type === this.constants.ORDER_TYPE_QUOTE_SHORT) {
                    this.webSocketService.emit('quote_created', {
                        quoteId: this.modelOrder.id,
                        exportId: this.modelOrder.export_id,
                        userId: this.modelOrder.frontend_user_id
                    });
                } else if (this.type === this.constants.ORDER_TYPE_SAMPLE_SHORT) {
                    this.webSocketService.emit('sample_request_created', true);
                }
            },
            (error: any) => {
            }
        );
    }


    getProductById(productId: number) {
        return this.productDetail.find(item => item.id === productId);
    }


    getCommentTooltip(comment: any): string {
        const htmlRegex = /<[^>]*>/g;
        let tempComment = comment ? comment.replace(htmlRegex, '').trim() : '';
        if (!tempComment) {
            return 'No comment added';
        }
        return comment ? comment : 'No comment added';
    }

    setCommentPlaceholder(comment: any) {
        if (comment) {
            return comment;
        }
        if (!comment && (this.isQuoteCancel || this.modelOrder?.is_archive == Constants.IS_ARCHIVE_YES)) {
            return 'No comment added';
        }
        if (!comment && !this.isQuoteCancel) {
            return 'Add your comment here...'
        }
    }

    getBackendUserList() {
        let requestParamModel = new RequestParamModel();
        requestParamModel.addDefaultFilters('status', 'A', 'number');
        requestParamModel.pageSize = 'all';
        this.usersService.getBackEndUser(requestParamModel)
            .pipe(
                map((dataList: any) => dataList.data.map((item: any) => ({
                    value: `${item.first_name ?? ''} ${item.last_name ?? ''}`,
                    id: item.id
                })))
            )
            .subscribe((users: { value: string, id: number }[]) => {
                this.users = users;
                // this.initializeQuillEditor(); // Initialize Quill editor after fetching users
            });
    }

    initializeQuillEditor(containers?: any) {
        if (!containers?.length) return
        containers.forEach((container, index) => {
            const quillEditor = new Quill(container.nativeElement, {
                modules: {
                    toolbar: false,
                    mention: {
                        allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
                        mentionDenotationChars: ["@"],
                        source: (searchTerm, renderList, mentionChar) => {
                            if (!quillEditor.isEnabled()) {
                                return;
                            }
                            if (mentionChar === "@") {
                                const matches = this.users.filter(user =>
                                    user.value.toLowerCase().includes(searchTerm.toLowerCase())
                                );
                                renderList(matches, searchTerm);
                            }
                        },
                        onSelect: (item, insertItem) => {
                            const user = this.users.find(u => u.value === item.value);
                            if (user) {
                                // this.selectedUsers.push(user);

                                // Initialize selectedUsers as an object if it's not already initialized
                                if (!this.selectedUsers) {
                                    this.selectedUsers = [];
                                }

                                // Ensure there's an array for the specific container ID
                                if (!this.selectedUsers[container.nativeElement.id]) {
                                    this.selectedUsers[container.nativeElement.id] = [];
                                }

                                // Push the user into the array for the specific container ID
                                this.selectedUsers[container.nativeElement.id].push(user);
                                insertItem(item);
                            }
                        }
                    }
                },
                placeholder: this.setCommentPlaceholder(null),
                theme: 'snow',
            });
            this.updateEditorState(quillEditor);
            // Set the Quill editor instance in the containers map
            this.quillEditorContainers[container.nativeElement.id] = quillEditor;

            /* ################### Set Existing Content From Database Into Editor Start ################### */
            if (this.modelOrder && this.modelOrder?.quote_product && this.modelOrder?.quote_product[index]?.note) {
                quillEditor.setContents(quillEditor.clipboard.convert({ html: this.modelOrder.quote_product[index].note }));
                this.message = this.modelOrder.quote_product[index].note;
                this.updateEditorState(quillEditor);

                const editorContent = this.getContentWithMentions(this.modelOrder?.quote_product[index]?.id);
                this.updateSelectedUsers(editorContent, this.modelOrder.quote_product[index].id, true);
            }
            /* ################### Set Existing Content From Database Into Editor End ################### */

            quillEditor.on('text-change', () => {
                this.isCancelButtonEnabled = false;
                const editorContent = this.getContentWithMentions(this.modelOrder.quote_product[index].id);
                let content = quillEditor.root.innerHTML;
                if (content === '<p><br></p>') {
                    content = ''; // Treat the editor as empty if it contains only <p><br></p>
                }
                this.modelOrder.quote_product[index].note = content;
                this.updateSelectedUsers(editorContent, this.modelOrder.quote_product[index].id);
            });
            // Add event listeners for both keyup and click events
            quillEditor.root.addEventListener('keyup', () => this.handleEditorEvent(index));
            quillEditor.root.addEventListener('click', () => this.handleEditorEvent(index));
            // Optional: Listen to Quill's text-change event, which captures all changes
            quillEditor.on('text-change', () => this.handleEditorEvent(index));
        });
    }

    handleEditorEvent(index: number) {
        clearTimeout(this.keyupTimeout);
        // Set a new timeout
        this.keyupTimeout = setTimeout(() => {
            this.onCommentKeyUp(this.modelOrder.quote_product[index].note, this.modelOrder.quote_product[index].id, this.modelOrder.quote_product[index].old_note);
        }, 2000); // 2000 milliseconds = 2 seconds
    }

    updateSelectedUsers(editorContent: string, containerId: number = null, isSetExistingUser = false) {
        if (containerId === null || containerId <= 0) {
            return;
        }

        const mentionRegex = /{{mention:([\w\s]+?)}}/g;
        const mentionedUserValues: string[] = [];
        let match;

        while ((match = mentionRegex.exec(editorContent)) !== null) {
            mentionedUserValues.push(match[1].trim());
        }

        if (!isSetExistingUser) {
            // Ensure this.selectedUsers[containerId] is initialized
            if (!this.selectedUsers[containerId]) {
                this.selectedUsers[containerId] = [];
            }

            // Get current selected user values
            const currentSelectedUserValues = this.selectedUsers[containerId]?.map(user => user.value) || [];

            // Identify users to add to selectedUsers
            const usersToAdd = this.users.filter(user =>
                mentionedUserValues.includes(user.value) && !currentSelectedUserValues.includes(user.value)
            );

            // Identify users to be removed from selectedUsers
            const usersToRemove = this.selectedUsers[containerId]?.filter(user =>
                !mentionedUserValues.includes(user.value)
            ) || [];

            // Update selectedUsers
            this.selectedUsers[containerId] = [
                ...this.selectedUsers[containerId].filter(user => mentionedUserValues.includes(user.value)),
                ...usersToAdd
            ];

            // Ensure this.removedUsers[containerId] is initialized
            if (!this.removedUsers[containerId]) {
                this.removedUsers[containerId] = [];
            }

            // Store removed users if they are not already in removedUsers[containerId]
            usersToRemove.forEach(user => {
                if (!this.removedUsers[containerId].some(r => r.id === user.id)) {
                    this.removedUsers[containerId].push({id: user.id, name: user.value});
                }
            });

            // Clean up removedUsers by removing users that are still selected
            this.removedUsers[containerId] = this.removedUsers[containerId].filter(removedUser =>
                !this.selectedUsers[containerId].some(selectedUser => selectedUser.id === removedUser.id)
            );
        } else {
            if (mentionedUserValues) {
                this.selectedUsers[containerId] = this.users.filter(user => mentionedUserValues.includes(user.value));
            }
        }
    }

    updateEditorState(quillEditor: Quill) {
        if (this.isQuoteCancel || this.isOrderCancel || this.modelOrder?.is_archive == this.constants.IS_ARCHIVE_YES || this.modelOrder?.is_expired == this.constants.IS_EXPIRED_YES) {
            quillEditor.enable(false);
            quillEditor.container.classList.add('disabled-editor'); // Add disabled class
        } else {
            quillEditor.enable(true);
            quillEditor.container.classList.remove('disabled-editor'); // Remove disabled class
        }
    }

    sendNotification(response: any) {
        /* ################### Send Notification To Selected Users Start ################### */
        if (Array.isArray(this.selectedUsers[response.id]) && this.selectedUsers[response.id].length > 0) {
            const editorContent = this.getContentWithMentions(response.id);

            // Regex pattern to match mentions in the format {{mention:Name}}
            const mentionRegex = /{{mention:([\w\s]+?)}}/g;

            // Remove mentions from the original note to get the message part
            let messagePart = editorContent.replace(mentionRegex, '').trim();

            // Iterate over selected users to send notifications
            this.selectedUsers[response.id]?.forEach((user: any) => {
                const mention = `@${user.value}`;

                // Construct the message string for this user
                const notificationMessage = `<span class="mention">${mention}</span> ${messagePart}`;

                // Prepare the request model for sending notification
                const requestModel: any = {
                    message: notificationMessage,
                    quote_product_id: response.id, // Adjust as per your response structure
                    from_user_id: this.loginUserId,
                    type: Constants.QUOTE_PRODUCT_NOTIFICATION,
                    to_user_id: user.id,
                };

                // Send notification with updated message containing mentions
                this.notificationService.sendNotification(requestModel).subscribe((notificationResponse) => {
                    // Handle notification response if needed
                    if (this.quoteProductExistingNotifications && this.quoteProductExistingNotifications.length > 0) {
                        // Check if element.id already exists in the array
                        const existingIndex = this.quoteProductExistingNotifications[response.id]?.findIndex(item => item?.id === notificationResponse.id);
                        if (existingIndex === -1) {
                            // Add new notification to the existing array
                            this.quoteProductExistingNotifications[response.id].push(notificationResponse);
                        } else {
                            // Ensure that this.quoteProductExistingNotifications[response.id] is initialized as an array
                            if (!Array.isArray(this.quoteProductExistingNotifications[response.id])) {
                                this.quoteProductExistingNotifications[response.id] = [];
                            }
                            // Add new notification to the existing array
                            this.quoteProductExistingNotifications[response.id].push(notificationResponse);
                        }
                    } else {
                        // Ensure that this.quoteProductExistingNotifications[response.id] is initialized as an array
                        if (!Array.isArray(this.quoteProductExistingNotifications[response.id])) {
                            this.quoteProductExistingNotifications[response.id] = [];
                        }
                        // Add new notification to the existing array
                        this.quoteProductExistingNotifications[response.id].push(notificationResponse);
                    }
                    this.subscribersService.updateUnreadNotification(this.unReadNotificationCount.toString());
                });
            });
        }
        /* ################### Send Notification To Selected Users End ################### */
    }

    getContentWithMentions(id: number): any {
        let content = '';
        if (this.quillEditorContainers && this.quillEditorContainers.length > 0 && id > 0) {
            if (this.quillEditorContainers[id]) {
                const delta = this.quillEditorContainers[id].getContents();
                delta.ops.forEach(op => {
                    if (typeof op.insert === 'string') {
                        content += op.insert;
                    } else if (op.insert.mention) {
                        content += `{{mention:${(op.insert.mention as any).value}}} `;
                    }
                });
            }
        }
        return content.trim();
    }

    /************************************
     * Quote product comment functions end
     ************************************/

    /************************************
     * Upload sample Image functions start
     ************************************/

    getSampleImage(product_image: any): string {
        const getKitPath = Constants.S3_PRODUCT_IMAGES_IMAGE_KIT_URL;
        if (product_image != '' && product_image != null) {
            return getKitPath + '/UPLOAD_SAMPLES_PACKING_IMAGES/' + product_image + this.constants.IMAGE_SIZE_50_X_50;
        }
        return null;
    }

    openUploadSampleImageModal(productRow: any) {
        if (this.modelOrder?.is_archive === Constants.IS_ARCHIVE_YES && productRow.samplePackingImages.length == 0) {
            this.alertService.error(Constants.UNABLE_SAMPLE_ORDER_DETAIL_CHANGE);
            return;
        }
        const instance = this.modalService.open(UploadSamplePackingImageModalNewComponent, {
            centered: false,
            windowClass: 'update-contact-modal upload-product-image-modal'
        });
        instance.componentInstance.productsRow = productRow;
        instance.closed.subscribe((result) => {
            if (result) {
                this.subscription = this.subscribersService.sampleImageDetails.subscribe((data) => {
                    if (data.id && data.original_file_name) {
                        let itemToUpdate = this.modelOrder.quote_product.find(item => item.id === data.quote_product_id);
                        if (itemToUpdate && itemToUpdate.samplePackingImages && itemToUpdate.samplePackingImages.length === 0) {
                            let newImage = {
                                quote_product_id: itemToUpdate.id,
                                id: data.id,
                                original_file_name: data.original_file_name,
                            };

                            itemToUpdate.samplePackingImages.push(newImage);
                        }
                    }
                });
            }
        })
    }

    deleteSamplePackingImage(sampleImageId: any) {
        if (this.modelOrder?.is_archive === Constants.IS_ARCHIVE_YES) {
            this.alertService.error(Constants.UNABLE_SAMPLE_ORDER_DETAIL_CHANGE);
            return;
        }
        this.modalRef = this.modalServices.show(ConfirmModalComponent);
        this.modalRef.content.title = 'Confirm Delete Sample Image';
        this.modalRef.content.message = Constants.DELETE_SAMPLE_REQUEST_IMAGE_CONSTANTS.BODY_MESSAGE;
        this.modalRef.setClass('delete-favourite-modal');

        this.modalRef.content.onClose.subscribe((result: any) => {
            if (result) {
                this.orderService.deleteSamplePackingImage(sampleImageId).subscribe(res => {
                        this.getOrderDetail();
                        this.alertService.success(Constants.SAMPLE_IMAGE_DELETE_SUCCESSFULLY);
                    },
                    (error: HttpErrorResponse) => {
                    });
            }
        })
    }

    /************************************
     * Upload sample Image functions end
     ************************************/

    /************************************
     * Other functions start
     ************************************/
    generateSticker(productsRow) {
        if (this.modelOrder?.is_archive === Constants.IS_ARCHIVE_YES) {
            this.alertService.error(Constants.UNABLE_SAMPLE_ORDER_DETAIL_CHANGE);
            return;
        }
        this.productServices.generateSticker(productsRow.product_id).subscribe(
            (response: any) => {
                if (isPlatformBrowser(this.platformId)) {
                    window.open(response, '_blank');
                }
                this.changeDetection.detectChanges();
            },
            (error: HttpErrorResponse) => {
                this.isLoading = false;
            }
        );
    }

    exportConsummableSummary() {
        let modalRef = this.modalService.open(QuoteInvoiceExportModalComponent, {
            centered: false,
            windowClass: 'donationModals quotes-model quote-order-export-pdf-model',
        });
        modalRef.componentInstance.quoteModel = this.modelOrderDetail;
        modalRef.componentInstance.isConsumableSummaryExportPopupOn = false;
        modalRef.componentInstance.typeForDownload = Constants.DOWNLOAD_CONSUMABLES;
        modalRef.closed.subscribe(() => {
        });
    }

    downloadConsummableSummaryPDF() {
        if (this.modelOrderDetail.id !== null && this.modelOrderDetail.id !== undefined && this.modelOrderDetail.id > 0) {
            this.isLoading = true;
            let params: any = {};
            params.id = this.modelOrderDetail.id;
            params.type = this.typeForDownload;
            // const url = this.orderService.downloadQuoteOrder(params);
            // if (isPlatformBrowser(this.platformId))
            //   window.location.assign(url);
            // this.isLoading = false;

            this.orderService.downloadQuoteOrder(params).subscribe((data: any) => {
                if (data !== null && data !== undefined && data !== "") {
                    let json = {
                        file_name: data
                    };
                    const url = this.orderService.downloadQuoteOrderNew(json);
                    if (isPlatformBrowser(this.platformId))
                        // window.location.assign(url);
                        window.open(url, '_blank');
                }
                this.isLoading = false;
                // OnPush: this HTTP callback doesn't mark the component for check, so without
                // this the `isLoading` reset never repaints and the loader spins forever even
                // though the download already finished.
                this.changeDetection.detectChanges();
            }, (error: HttpErrorResponse) => {
                if (error.message) {
                    this.router.navigate(['/']);
                }
                this.isLoading = false;
                this.changeDetection.detectChanges();
            });
        }
    }

    copySku(sku: string) {
        if (sku) {
            const selBox = document.createElement('textarea');
            selBox.style.position = 'fixed';
            selBox.style.left = '0';
            selBox.style.top = '0';
            selBox.style.opacity = '0';
            selBox.value = sku;

            document.body.appendChild(selBox);
            selBox.focus();
            selBox.select();
            document.execCommand('copy');
            document.body.removeChild(selBox);

            this.alertService.success(Constants.SKU_COPIED);
        }
    }

    getFormattedDiscount(discount?: number | null): string {
        if (discount === null || discount === undefined) {
            return '20%';
        }
        return discount % 1 === 0 ? `${discount}%` : `${discount.toFixed(2)}%`;
    }

    /************************************
     * Other functions end
     ************************************/

    /************************************
     * Product row drag and drop functions start
     ************************************/

    onProductRowDragStart(productRow: any) {
        this.draggedProductIndex = productRow.order_index;
    }

    onProductRowDragEnd() {
        this.draggedProductIndex = null;
        this.changeDetection.detectChanges();
    }

    onProductRowDrop(event: DndDropEvent, targetRow: any) {
        this.draggedProductIndex = null;
        const draggedProduct = event.data;
        const droppedOnProduct = targetRow;

        const draggedIndex = this.modelOrder.quote_product.findIndex(product => product.id === draggedProduct.id);
        const droppedIndex = this.modelOrder.quote_product.findIndex(product => product.id === droppedOnProduct.id);

        if (draggedIndex !== -1 && droppedIndex !== -1 && draggedIndex !== droppedIndex) {
            if (draggedIndex < droppedIndex) {
                this.modelOrder.quote_product.forEach((product, index) => {
                    if (index > draggedIndex && index <= droppedIndex) {
                        product.order_index -= 1;
                    }
                });
            } else {
                this.modelOrder.quote_product.forEach((product, index) => {
                    if (index >= droppedIndex && index < draggedIndex) {
                        product.order_index += 1;
                    }
                });
            }

            this.modelOrder.quote_product[draggedIndex].order_index = droppedIndex + 1;
            this.modelOrder.quote_product.forEach(product => {
                product.isCommentExpanded = product.note && product.note.trim().length > 0 ? true : false;
                product.isStockExpanded = false;
            });
            this.modelOrder.quote_product.sort((a, b) => a.order_index - b.order_index);

            this.modelOrder.quote_product.forEach((product, index) => {
                this.quoteService.updateQuoteProduct({order_index: product.order_index}, product.id)
                    .subscribe(response => {
                        if (response) {
                            if (index === this.modelOrder.quote_product.length - 1) {
                                this.alertService.success(Constants.PRODUCT_ORDER_SEQUENCE_UPDATE_SUCCESS_MESSAGE);
                                this.initializeQuillEditor();
                            }
                        }
                    })
            })
        } else {
            this.draggedProductIndex = null;
        }
    }

    onProductRowDragOver(event: any, targetRow: any) {
        event.preventDefault();
        const hoverProduct = targetRow;
        const hoverIndex = hoverProduct.order_index;

        if (this.hoveredProductIndex !== hoverIndex) {
            this.hoveredProductIndex = hoverIndex;
            this.changeDetection.detectChanges();
        }
    }

    /************************************
     * Product row drag and drop functions end
     ************************************/

    toggleStatusHistory(productsRow) {
        productsRow.isStatusSectionVisible = !productsRow.isStatusSectionVisible;
    }

    getRedirectUrl(modelOrder: any): string {
        if (!modelOrder?.id) {
            return '';
        }

        switch (modelOrder?.type) {
            case Constants.ORDER_TYPE_SAMPLE_SHORT:
                return `/sample-requests/view/${modelOrder.id}`;
            case Constants.ORDER_TYPE_QUOTE_SHORT:
                return `/quotes/view/${modelOrder.id}`;
            case Constants.ORDER_TYPE_ORDER_SHORT:
                return `/orders/view/${modelOrder.id}`;
            default:
                // fallback orders
                return '';
        }
    }

    /**
     * Sort quote products and assign displayIndex for parent and child products
     * Parent products get index like: 1, 2, 3
     * Child products (split) get index like: 2.1, 2.2, 2.3
     * This matches the logic used in Quote/Order Detail screens
     */
    private sortQuoteProducts(items: any[]): any[] {
        if (!Array.isArray(items) || items.length === 0) return [];

        // Get root products (products without parent_id)
        const roots = items.filter(it => !it.parent_id);

        // Sort root products by order_index, then by created_at
        roots.sort((a, b) => {
            const aOrder = a.order_index ?? 0;
            const bOrder = b.order_index ?? 0;
            if (aOrder !== bOrder) return aOrder - bOrder;
            return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        });

        const result: any[] = [];
        let rootCounter = 0;

        for (const root of roots) {
            rootCounter += 1;
            root.displayIndex = String(rootCounter);
            result.push(root);

            // Collect children recursively, flattening
            const children: any[] = [];
            const collectChildren = (parentId: number) => {
                const kids = items.filter(it => it.parent_id === parentId)
                    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
                for (const kid of kids) {
                    children.push(kid);
                    collectChildren(kid.id);
                }
            };

            collectChildren(root.id);

            // Assign displayIndex to children (e.g., 2.1, 2.2, 2.3)
            let childCounter = 0;
            for (const child of children) {
                childCounter += 1;
                child.displayIndex = `${root.displayIndex}.${childCounter}`;
                result.push(child);
            }
        }

        return result;
    }
}


